import { isAfter } from 'date-fns'
import { Sparkle } from 'lucide-react'

// import * as ManagementReport from '@fortem-monorepo/web-sdk'

import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { FiltersProvider, useFilters } from '@fortem-monorepo/react-spa'
import { createFilters } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { ManagementReport } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { FUNDS } from '@/constants/links'
import { AttributionsGrid } from '@/pages/funds/management-report/grids/AttributionsGrid'
import { FixedIncomeGrid } from '@/pages/funds/management-report/grids/FixedIncomeGrid'
import { ReturnsGrid } from '@/pages/funds/management-report/grids/ReturnsGrid'
import { RiskGrid } from '@/pages/funds/management-report/grids/RiskGrid'
import { TrackingDifferencesGrid } from '@/pages/funds/management-report/grids/TrackingDifferencesGrid'
import { useManagementReportQuery } from '@/pages/funds/management-report/query'
import { useFunds } from '@/react-query/queries/funds/funds'

const filters = createFilters([
  {
    name: 'value_date',
    type: 'date',
    defaultValue: getTMinus1(),
    params: true,
  },
  {
    name: 'fund_name',
    type: 'select',
    defaultValue: undefined,
    params: true,
  },
])

export interface ManagementReportPageProps {}

export const ManagementReportPage: React.FC<ManagementReportPageProps> = ({}) => {
  return (
    <Page
      metadata={{ title: 'Management Report' }}
      permissions={FUNDS.MANAGEMENT_REPORT.permissions}
    >
      <FiltersProvider filters={filters}>
        <PageContent />
      </FiltersProvider>
    </Page>
  )
}

interface PageContentProps {}

const PageContent: React.FC<PageContentProps> = ({}) => {
  const { values, registerFilter } = useFilters<typeof filters>()
  const { data: funds, query: fundsQuery } = useFunds()

  const params = {
    fund_name: values.fund_name?.toUpperCase(),
    value_date: values.value_date ? toISO8601(values.value_date) : null,
  } as ManagementReport.GetManagementReportParams

  const query = useManagementReportQuery(params)

  return (
    <>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <DatePicker
            size="sm"
            {...registerFilter('value_date')}
            disabled={(date) => isAfter(date, new Date())}
          />
          <SearchSelect
            size="sm"
            placeholder={'Select a fund...'}
            className={'min-w-40'}
            loading={fundsQuery.isFetching}
            value={values.fund_name}
            {...registerFilter('fund_name')}
            options={funds.map((f) => ({
              label: f.fund_name,
              value: f.fund_name,
            }))}
          />
        </div>
      </HeaderConfigBar>

      {!query.isLoading && query.data && (
        <HeaderConfigBar>
          <span className="text-sm text-muted-foreground">
            Showing results from <span className={'font-medium'}>{query.data.start_date}</span> to{' '}
            <span className={'font-medium'}>{query.data.end_date}</span>
          </span>
        </HeaderConfigBar>
      )}

      {!params.fund_name ? (
        <div className={'flex flex-1'}>
          <div className="m-auto p-10 border rounded-lg bg-sidebar">
            <div className="flex flex-col gap-4 items-center">
              <Sparkle />
              <p>Select a fund to generate the management report</p>
            </div>
          </div>
        </div>
      ) : (
        <ViewContainer className={'flex flex-col gap-4'}>
          <ReturnsGrid params={params} />
          <div className="flex items-start gap-4">
            <RiskGrid params={params} />
            <AttributionsGrid params={params} />
          </div>
          <FixedIncomeGrid params={params} />
          {(query.data?.tracking_differences?.length ?? 0) > 0 && (
            <TrackingDifferencesGrid params={params} />
          )}
        </ViewContainer>
      )}
    </>
  )
}
